import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { loadLanguage } from "@/i18n";

export const SUPPORTED_LANGS = ["en", "ar", "hi", "ru"] as const;
export type SupportedLang = (typeof SUPPORTED_LANGS)[number];
export const PREFIXED_LANGS: SupportedLang[] = ["ar", "hi", "ru"];

/** Extract language from a pathname. English (no prefix) returns "en". */
export const getLangFromPath = (pathname: string): SupportedLang => {
  const seg = pathname.split("/")[1];
  if (seg === "ar" || seg === "hi" || seg === "ru") return seg;
  return "en";
};

/** Strip /ar or /hi prefix and return the canonical (English) path. */
export const stripLangPrefix = (pathname: string): string => {
  const seg = pathname.split("/")[1];
  if (seg === "ar" || seg === "hi" || seg === "ru") {
    const rest = pathname.slice(seg.length + 1);
    return rest === "" ? "/" : rest;
  }
  return pathname || "/";
};

/** Add language prefix to a canonical (English) path. */
export const withLangPrefix = (path: string, lang: SupportedLang): string => {
  const clean = path.startsWith("/") ? path : `/${path}`;
  if (lang === "en") return clean;
  if (clean === "/") return `/${lang}`;
  return `/${lang}${clean}`;
};

/**
 * Hook returning a function that prefixes any internal href with the
 * current language. Use for `<Link to={href("/pricing")}>`.
 */
export const useLocalizedHref = () => {
  const { i18n } = useTranslation();
  const lang = (i18n.language as SupportedLang) || "en";
  return (path: string) => {
    // Preserve query strings / hashes by splitting on the first ? or #.
    const match = path.match(/^([^?#]*)(.*)$/);
    if (!match) return withLangPrefix(path, lang);
    return `${withLangPrefix(match[1] || "/", lang)}${match[2]}`;
  };
};

/**
 * Hook that keeps i18next + <html lang|dir> in sync with the URL prefix.
 * Mounted once at the app root.
 */
export const useSyncLanguageWithUrl = () => {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  useEffect(() => {
    const lang = getLangFromPath(pathname);
    if (i18n.language !== lang) {
      loadLanguage(lang).then(() => i18n.changeLanguage(lang));
    }
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [pathname, i18n]);
};

/** Hook returning a function that navigates to the same page in another language. */
export const useSwitchLanguage = () => {
  const navigate = useNavigate();
  const { pathname, search, hash } = useLocation();
  return (lang: SupportedLang) => {
    const canonical = stripLangPrefix(pathname);
    const next = withLangPrefix(canonical, lang) + search + hash;
    navigate(next);
  };
};
